/**
 * Servicio de Almacenamiento Durable Offline para Puntos GPS en SQLite.
 * Totalmente desacoplado de las colas de visitas, formularios y fotos.
 * 
 * Reglas de gobernanza:
 * - Identificador único UUID v4 por cada punto de captura.
 * - Timestamp inmutable del momento real de la lectura GPS.
 * - Encolado persistente en SQLite nativo (con fallback IndexedDB).
 * - Purgado local estrictamente condicionado a confirmación explícita (ACK) del servidor (HTTP 200).
 */

import { OFFLINE_STORES, getOfflineStorageDriver } from "./offlineStorageAdapter.js";

function generateUuid() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return "gps_" + Date.now() + "_" + Math.random().toString(36).substring(2, 9);
}

/**
 * Encola un punto GPS de forma durable en SQLite.
 *
 * @param {object} point - { latitude, longitude, accuracy, batteryLevel, visitaId, userId, timestamp }
 * @returns {Promise<object>} El punto persistido con su UUID asignado.
 */
export async function enqueueGpsPoint(point) {
    if (!point || point.latitude == null || point.longitude == null) {
        throw new Error("[gpsOfflineStorage] Coordenadas inválidas para encolar");
    }

    const driver = getOfflineStorageDriver();
    await driver.init();

    const uuid = String(point.uuid || point.id || generateUuid());
    const nowIso = new Date().toISOString();

    const record = {
        id: uuid,
        uuid: uuid,
        userId: point.userId != null ? Number(point.userId) : null,
        user_id: point.userId != null ? Number(point.userId) : null,
        visitaId: point.visitaId != null ? Number(point.visitaId) : null,
        visita_id: point.visitaId != null ? Number(point.visitaId) : null,
        latitude: Number(point.latitude),
        longitude: Number(point.longitude),
        accuracy: point.accuracy != null ? Number(point.accuracy) : null,
        batteryLevel: point.batteryLevel != null ? Number(point.batteryLevel) : null,
        battery_level: point.batteryLevel != null ? Number(point.batteryLevel) : null,
        timestamp: String(point.timestamp || nowIso),
        status: "pending",
        retryCount: 0,
        retry_count: 0,
        createdAt: nowIso,
        created_at: nowIso,
        updatedAt: nowIso,
        updated_at: nowIso,
    };

    await driver.put(OFFLINE_STORES.GPS, record);
    return record;
}

/**
 * Obtiene los puntos GPS pendientes de envío ordenados cronológicamente.
 *
 * @param {number} limit - Cantidad máxima de puntos a recuperar (default 50).
 * @returns {Promise<Array<object>>}
 */
export async function getPendingGpsPoints(limit = 50) {
    const driver = getOfflineStorageDriver();
    await driver.init();

    const all = await driver.getAll(OFFLINE_STORES.GPS);
    const pending = (all || []).filter((item) => item.status === "pending" || item.status === "failed");

    // Orden cronológico ascendente (más antiguos primero para respetar secuencia de trayecto)
    pending.sort((a, b) => {
        const tA = new Date(a.timestamp || 0).getTime();
        const tB = new Date(b.timestamp || 0).getTime();
        return tA - tB;
    });

    return pending.slice(0, limit);
}

/**
 * Confirma y purga localmente los puntos GPS que el servidor confirmó con HTTP 200.
 *
 * @param {Array<string|object>} confirmedItems - Array de UUIDs o de objetos con propiedad uuid/id.
 * @returns {Promise<number>} Cantidad de puntos eliminados.
 */
export async function confirmGpsPoints(confirmedItems) {
    if (!Array.isArray(confirmedItems) || confirmedItems.length === 0) {
        return 0;
    }

    const driver = getOfflineStorageDriver();
    await driver.init();

    let deletedCount = 0;
    for (const item of confirmedItems) {
        const id = typeof item === "string" ? item : (item.uuid || item.id);
        if (id) {
            try {
                await driver.delete(OFFLINE_STORES.GPS, id);
                deletedCount++;
            } catch (err) {
                console.warn("[gpsOfflineStorage] Error al eliminar punto confirmado:", id, err);
            }
        }
    }

    return deletedCount;
}

/**
 * Marca puntos como fallidos incrementando su contador de reintentos.
 *
 * @param {Array<string|object>} failedItems - Array de UUIDs o de objetos.
 * @returns {Promise<number>}
 */
export async function markGpsPointsFailed(failedItems) {
    if (!Array.isArray(failedItems) || failedItems.length === 0) {
        return 0;
    }

    const driver = getOfflineStorageDriver();
    await driver.init();

    let updatedCount = 0;
    for (const item of failedItems) {
        const id = typeof item === "string" ? item : (item.uuid || item.id);
        if (!id) continue;

        try {
            const existing = await driver.get(OFFLINE_STORES.GPS, id);
            if (existing) {
                const currentRetries = Number(existing.retryCount || existing.retry_count || 0);
                const updated = {
                    ...existing,
                    status: "failed",
                    retryCount: currentRetries + 1,
                    retry_count: currentRetries + 1,
                    updatedAt: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                };
                await driver.put(OFFLINE_STORES.GPS, updated);
                updatedCount++;
            }
        } catch (err) {
            console.warn("[gpsOfflineStorage] Error al actualizar estado fallido:", id, err);
        }
    }

    return updatedCount;
}

/**
 * Retorna la cantidad total de puntos GPS pendientes en la base local.
 *
 * @returns {Promise<number>}
 */
export async function getPendingGpsCount() {
    const driver = getOfflineStorageDriver();
    await driver.init();
    return driver.count(OFFLINE_STORES.GPS, item => item.status === 'pending' || item.status === 'failed');
}
